import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, AlertCircle } from "lucide-react";


const GRADE_TONE = {
    Royal:       { ring: "ring-amber-400/60",   text: "text-amber-300",   bg: "from-amber-500/15 to-yellow-500/5", chip: "bg-amber-400 text-black" },
    Autonomous:  { ring: "ring-emerald-400/50", text: "text-emerald-300", bg: "from-emerald-500/15 to-emerald-500/5", chip: "bg-emerald-400 text-black" },
    Healthy:     { ring: "ring-sky-400/50",     text: "text-sky-300",     bg: "from-sky-500/15 to-sky-500/5",     chip: "bg-sky-400 text-black" },
    Attention:   { ring: "ring-amber-500/60",   text: "text-amber-400",   bg: "from-amber-500/15 to-orange-500/5", chip: "bg-amber-500 text-black" },
    "Hands-on":  { ring: "ring-red-500/60",     text: "text-red-400",     bg: "from-red-500/15 to-red-500/5",     chip: "bg-red-500 text-white" },
};


export default function AutonomyScoreCard() {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/admin/autonomy-score")
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || e.message));
    }, []);

    if (error) {
        return (
            <div className="bg-card border border-red-700/40 p-6 rounded-sm text-sm text-red-300" data-testid="autonomy-score-error">
                Autonomy score unavailable: {error}
            </div>
        );
    }

    if (!data) {
        return (
            <div className="bg-card border border-white/10 p-6 rounded-sm animate-pulse" data-testid="autonomy-score-loading">
                <div className="h-5 w-40 bg-white/10 rounded mb-3" />
                <div className="h-20 w-full bg-white/5 rounded" />
            </div>
        );
    }

    const score = Number(data.score || 0);
    const tone = GRADE_TONE[data.grade] || GRADE_TONE.Healthy;
    // Dash-offset for a 100-radius circle
    const circumference = 2 * Math.PI * 42;
    const offset = circumference - (score / 100) * circumference;

    return (
        <div
            data-testid="autonomy-score-card"
            className={`relative bg-gradient-to-br ${tone.bg} border border-white/10 ${tone.ring} ring-1 rounded-sm p-6 lg:p-8 overflow-hidden`}
        >
            <div className="flex items-start gap-6 flex-wrap lg:flex-nowrap">
                {/* Radial gauge */}
                <div className="relative w-32 h-32 flex-shrink-0" data-testid="autonomy-score-gauge">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
                        <circle
                            cx="50" cy="50" r="42"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="7"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={offset}
                            className={`transition-all duration-700 ${tone.text}`}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className={`font-serif text-4xl leading-none ${tone.text}`} data-testid="autonomy-score-number">
                            {Math.round(score)}
                        </div>
                        <div className="text-[9px] uppercase tracking-[0.24em] text-muted-foreground mt-1">/ 100</div>
                    </div>
                </div>

                {/* Headline + grade */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-2">
                        <Sparkles size={11} /> Autonomy Score · last 7 days
                    </div>
                    <h3 className="font-serif text-2xl lg:text-3xl text-foreground mb-2">
                        {data.grade === "Royal" ? "Running on autopilot" :
                         data.grade === "Autonomous" ? "Mostly autonomous" :
                         data.grade === "Healthy" ? "Steady — minor touches" :
                         data.grade === "Attention" ? "Needs your attention" :
                         "Hands-on this week"}
                    </h3>
                    <div className="flex items-center gap-3 flex-wrap">
                        <span
                            data-testid={`autonomy-grade-${data.grade?.toLowerCase()}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.26em] font-semibold ${tone.chip}`}
                        >
                            {data.grade}
                        </span>
                        <button
                            data-testid="autonomy-score-toggle"
                            onClick={() => setExpanded((e) => !e)}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.26em] text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <TrendingUp size={11} />
                            {expanded ? "Hide" : "Show"} breakdown
                        </button>
                    </div>
                </div>
            </div>

            {expanded && (
                <div className="mt-6 pt-6 border-t border-white/10 grid gap-3" data-testid="autonomy-score-breakdown">
                    {data.components.map((c) => {
                        const dragging = c.score < c.weight * 0.6;
                        const fill = (c.score / c.weight) * 100;
                        return (
                            <div key={c.id} className="grid grid-cols-[1fr,auto] gap-x-3 gap-y-1 items-center" data-testid={`autonomy-component-${c.id}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-foreground">{c.label}</span>
                                    {dragging && <AlertCircle size={11} className="text-amber-400" />}
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                                    {c.score.toFixed(0)} / {c.weight}
                                </div>
                                <div className="col-span-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${
                                            dragging ? "bg-amber-500" : "bg-[#FFD700]"
                                        }`}
                                        style={{ width: `${Math.max(0, Math.min(100, fill))}%` }}
                                    />
                                </div>
                                <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    {c.rate_pct}% {c.d != null ? `· ${c.n} / ${c.d}` : `· ${c.n} stale`}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
